// משתמשים — who can log in, and in what role. The one screen that is partner-only.
//
// ⚠ THE GUARD IS THE SERVER'S, NOT THIS FILE'S. Every call here goes to `/admin/users`,
// which is mounted partner-only in the API's route table; a secretary opening this route
// by typing the URL gets 403 from every request it makes. The role check in App.js keeps
// the screen out of her way — it is not what keeps her out of the data.
//
// WHAT THE SCREEN REFUSES TO OFFER is as much the design as what it offers. A partner is
// shown read-only: no role change, no deactivation, no delete. That is not a limitation of
// the form, it is the business — see ./roles.js for the share-split reason, and the
// server's guards.js for what deactivating one would do to the settlement.
import { useState } from 'react';
import useAsync from '../../hooks/useAsync';
import he from '../../i18n/he';
import { usersAdminApi } from '../../api';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/PageHeader';
import AsyncBoundary from '../../components/Loader';
import DataTable from '../../components/DataTable';
import Button from '../../components/Button';
import UserModal from './UserModal';
import PasswordModal from './PasswordModal';
import StatusModal from './StatusModal';
import GrantsModal from './GrantsModal';
import { roleLabel, isPartner, ROLE } from './roles';

export default function Users() {
  const { partner: me } = useAuth();
  const [reloadKey, setReloadKey] = useState(0);
  const { data, loading, error } = useAsync(() => usersAdminApi.list(), [reloadKey]);
  const [editing, setEditing] = useState(null);      // user | {} for new
  const [passwordFor, setPasswordFor] = useState(null);
  const [status, setStatus] = useState(null);        // { user, mode }
  const [grantsFor, setGrantsFor] = useState(null);
  const [notice, setNotice] = useState(null);

  const users = data || [];
  const reload = () => setReloadKey((k) => k + 1);

  const columns = [
    {
      key: 'name',
      label: he.users.name,
      render: (u) => (
        <>
          {/* The person's calendar colour, as the edge of their name. */}
          <span className="user-name" style={{ '--user-color': u.color || undefined }}>
            {u.name}
          </span>
          {u.id === me.id && <span className="muted small-note"> · {he.users.you}</span>}
        </>
      ),
    },
    { key: 'email', label: he.users.email, render: (u) => <span className="muted">{u.email}</span> },
    { key: 'role', label: he.users.role, render: (u) => roleLabel(u.role) },
    {
      key: 'status',
      label: he.users.status,
      render: (u) => (
        <span className={`badge ${u.is_active ? 'green' : 'gray'}`}>
          {u.is_active ? he.users.active : he.users.inactive}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      action: true,
      render: (u) => {
        // A partner's row carries an explanation instead of buttons. Saying WHY up front
        // beats a refusal after the click — and the reason is real, not a policy.
        // A partner is changed from nowhere on this screen — the cell stays empty rather
        // than carrying a sentence explaining that.
        if (isPartner(u)) return null;
        return (
          <div className="row user-actions">
            <Button variant="subtle" small onClick={() => setEditing(u)}>{he.common.edit}</Button>
            {/* Only a content writer has anything to grant: a partner and a secretary
                already see the whole system, so the button would imply a restriction
                that does not exist — and the server refuses the write for them too. */}
            {u.role === ROLE.LIMITED && (
              <Button variant="subtle" small onClick={() => setGrantsFor(u)}>{he.users.grants}</Button>
            )}
            <Button variant="subtle" small onClick={() => setPasswordFor(u)}>
              {he.users.resetPassword}
            </Button>
            {u.is_active ? (
              <Button variant="subtle" small onClick={() => setStatus({ user: u, mode: 'deactivate' })}>
                {he.users.deactivate}
              </Button>
            ) : (
              <Button
                variant="subtle"
                small
                onClick={async () => { await usersAdminApi.setActive(u.id, true); reload(); }}
              >
                {he.users.reactivate}
              </Button>
            )}
            <Button variant="danger" small onClick={() => setStatus({ user: u, mode: 'remove' })}>
              {he.users.remove}
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader title={he.users.title}>
        <Button onClick={() => setEditing({})}>{he.users.add}</Button>
      </PageHeader>

      {/* Stated rather than implied: the screen says how many sessions it cut, because
          "they are locked out now" is the whole point of the act. */}
      {notice && <p className="muted small-note">{notice}</p>}

      <AsyncBoundary loading={loading} error={error}>
        <DataTable
          columns={columns}
          rows={users}
          rowClass={(u) => (u.is_active ? '' : 'muted')}
          empty={{ title: he.users.noUsers }}
        />
      </AsyncBoundary>

      {editing && (
        <UserModal
          user={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); setNotice(null); reload(); }}
        />
      )}
      {grantsFor && (
        <GrantsModal
          user={grantsFor}
          onClose={() => setGrantsFor(null)}
          onSaved={() => setNotice(he.users.grantsSaved)}
        />
      )}
      {passwordFor && (
        <PasswordModal
          user={passwordFor}
          onClose={() => setPasswordFor(null)}
          onSaved={() => { setPasswordFor(null); reload(); }}
        />
      )}
      {status && (
        <StatusModal
          user={status.user}
          mode={status.mode}
          onClose={() => setStatus(null)}
          onDone={(result) => {
            setNotice(status.mode === 'deactivate'
              ? he.users.deactivated(status.user.name, result.endedSessions)
              : null);
            setStatus(null);
            reload();
          }}
        />
      )}
    </>
  );
}
